"use client";

import { PhotoIcon, PencilIcon } from "@heroicons/react/24/outline";
import { updateCreatorImageAction } from "@/app/actions";
import { UploaderWrapper } from "./uploader-wrapper";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface ProfileBannerUploaderProps {
  creatorId: number;
  existingImageUrl?: string;
  className?: string;
}

export function ProfileBannerUploader({
  creatorId,
  existingImageUrl,
  className = "",
}: Readonly<ProfileBannerUploaderProps>) {
  const t = useTranslations("ProfileBannerUploader");
  const onUpload = (url: string) =>
    updateCreatorImageAction(creatorId, url, "banner");
  const onRemove = () => updateCreatorImageAction(creatorId, "", "banner");

  return (
    <UploaderWrapper
      aspectRatio={4 / 1}
      existingImageUrl={existingImageUrl}
      onUpload={onUpload}
      onRemove={onRemove}
    >
      {({ currentImageUrl, openUploadModal }) => (
        <div className={`${className}`}>
          {currentImageUrl ? (
            <button
              onClick={openUploadModal}
              className="relative h-full w-full cursor-pointer overflow-hidden rounded-lg border-2 border-zinc-700 transition-colors duration-200 hover:border-zinc-500"
            >
              <Image
                src={currentImageUrl}
                alt={t("alt")}
                fill
                className="object-cover"
                sizes="100vw"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/5 transition-all duration-200 group-hover:bg-black/30">
                <PencilIcon className="h-8 w-8 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
              </div>
            </button>
          ) : (
            <button
              onClick={openUploadModal}
              className="relative flex h-full w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 transition-all duration-200 hover:border-zinc-300 hover:bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:border-zinc-800 dark:hover:bg-zinc-700"
            >
              <PhotoIcon className="h-16 w-16 text-zinc-400" />
              <span className="mt-2 text-sm text-zinc-500">
                {t("uploadBanner")}
              </span>
              <span className="mt-1 text-xs text-gray-400">
                {t("recommended")}
              </span>
            </button>
          )}
        </div>
      )}
    </UploaderWrapper>
  );
}
